'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface League {
  id: string;
  name: string;
  sport: string;
  status: 'active' | 'ready' | 'planned';
  icon: string;
  color: string;
  providers: string[];
  modules: string[];
}

interface Provider {
  id: string;
  name: string;
  sports: string[];
  status: 'connected' | 'ready' | 'planned';
  endpoints: number;
  rateLimit: string;
}

const LEAGUES: League[] = [
  {
    id: 'nhl', name: 'NHL', sport: 'Hockey', status: 'active', icon: 'TrophyIcon', color: 'text-primary',
    providers: ['API-Hockey', 'The Odds API'],
    modules: ['Live Games', 'Standings', 'Player Stats', 'Goalie Analytics', 'xG Models', 'Monte Carlo'],
  },
  {
    id: 'nba', name: 'NBA', sport: 'Basketball', status: 'ready', icon: 'TrophyIcon', color: 'text-orange-400',
    providers: ['API-Basketball', 'The Odds API'],
    modules: ['Live Games', 'Standings', 'Player Props', 'Pace Analytics', 'Efficiency Models'],
  },
  {
    id: 'nfl', name: 'NFL', sport: 'Football', status: 'ready', icon: 'TrophyIcon', color: 'text-green-400',
    providers: ['API-NFL', 'The Odds API'],
    modules: ['Game Logs', 'Standings', 'Player Props', 'EPA Models', 'DVOA Analytics'],
  },
  {
    id: 'mlb', name: 'MLB', sport: 'Baseball', status: 'planned', icon: 'TrophyIcon', color: 'text-yellow-400',
    providers: ['API-Baseball', 'The Odds API'],
    modules: ['Game Logs', 'Standings', 'Pitcher Analytics', 'Statcast Models', 'wOBA/xFIP'],
  },
];

const PROVIDERS: Provider[] = [
  { id: 'api-hockey', name: 'API-Hockey (api-sports.io)', sports: ['NHL'], status: 'connected', endpoints: 12, rateLimit: '100/day' },
  { id: 'odds-api', name: 'The Odds API', sports: ['NHL', 'NBA', 'NFL', 'MLB'], status: 'connected', endpoints: 8, rateLimit: '500/month' },
  { id: 'api-basketball', name: 'API-Basketball', sports: ['NBA'], status: 'ready', endpoints: 10, rateLimit: '100/day' },
  { id: 'api-nfl', name: 'API-NFL', sports: ['NFL'], status: 'ready', endpoints: 9, rateLimit: '100/day' },
  { id: 'api-baseball', name: 'API-Baseball', sports: ['MLB'], status: 'planned', endpoints: 11, rateLimit: '100/day' },
];

const CORE_MODULES = [
  { name: 'League Engine', desc: 'Abstraction multi-sports avec routing dynamique par league_id', status: 'active', icon: 'CpuChipIcon' },
  { name: 'Analytics Core', desc: 'Pipeline unifié xG, Monte Carlo, projections — agnostique au sport', status: 'active', icon: 'ChartBarIcon' },
  { name: 'Market Engine', desc: 'Ingestion cotes multi-provider, normalisation, détection anomalies', status: 'active', icon: 'ArrowTrendingUpIcon' },
  { name: 'AI Copilot', desc: 'GPT-4 avec context injection sport-spécifique par league', status: 'active', icon: 'SparklesIcon' },
  { name: 'Sentinel Monitor', desc: 'Surveillance autonome — blessures, alignements, confirmations', status: 'active', icon: 'ShieldCheckIcon' },
  { name: 'WebSocket Layer', desc: 'Streaming temps réel, reconnexion auto, worker queues', status: 'ready', icon: 'SignalIcon' },
  { name: 'Edge Cache', desc: 'CDN edge caching, TTL adaptatif par type de données', status: 'ready', icon: 'ServerIcon' },
  { name: 'ML Parallelizer', desc: 'Calculs Monte Carlo parallélisés via Web Workers', status: 'ready', icon: 'BoltIcon' },
];

const PERF_METRICS = [
  { label: 'API Response', value: '< 120ms', target: '< 200ms', status: 'good' },
  { label: 'Monte Carlo 10k', value: '< 600ms', target: '< 1s', status: 'good' },
  { label: 'Live Data Refresh', value: '30s', target: '30s', status: 'good' },
  { label: 'Cache Hit Rate', value: '87%', target: '> 80%', status: 'good' },
  { label: 'WebSocket Latency', value: '< 50ms', target: '< 100ms', status: 'good' },
  { label: 'ML Calc (parallel)', value: '< 200ms', target: '< 500ms', status: 'good' },
];

export default function MultiSportClient() {
  const [activeTab, setActiveTab] = useState<'leagues' | 'providers' | 'core' | 'performance'>('leagues');

  const STATUS_CONFIG = {
    active: { color: 'text-positive', bg: 'bg-positive-subtle', border: 'border-positive/30', label: 'ACTIF' },
    ready: { color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', label: 'PRÊT' },
    planned: { color: 'text-muted-foreground', bg: 'bg-card', border: 'border-border', label: 'PLANIFIÉ' },
    connected: { color: 'text-positive', bg: 'bg-positive-subtle', border: 'border-positive/30', label: 'CONNECTÉ' },
    good: { color: 'text-positive', icon: 'CheckCircleIcon' },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 flex items-center justify-center">
            <Icon name="GlobeAltIcon" size={20} className="text-cyan-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Multi-Sport Architecture</h1>
            <p className="text-xs text-muted-foreground font-mono-data">League Engine · NBA/NFL/MLB Ready · Unified Analytics Core</p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-positive-subtle border border-positive/20">
          <span className="w-1.5 h-1.5 rounded-full bg-positive" />
          <span className="text-xs font-mono-data text-positive">NHL PRODUCTION</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-card border border-border rounded-xl p-1 w-fit">
        {(['leagues', 'providers', 'core', 'performance'] as const).map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all capitalize ${activeTab === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {t === 'leagues' ? 'Leagues' : t === 'providers' ? 'Providers' : t === 'core' ? 'Core Modules' : 'Performance'}
          </button>
        ))}
      </div>

      {/* Leagues */}
      {activeTab === 'leagues' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {LEAGUES.map(league => {
            const s = STATUS_CONFIG[league.status];
            return (
              <div key={league.id} className={`bg-card border rounded-xl p-4 ${league.status === 'active' ? 'border-primary/30' : 'border-border'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-card border border-border flex items-center justify-center`}>
                      <span className={`text-lg font-bold font-mono-data ${league.color}`}>{league.id.toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-foreground">{league.name}</div>
                      <div className="text-xs text-muted-foreground">{league.sport}</div>
                    </div>
                  </div>
                  <span className={`text-2xs px-2 py-0.5 rounded-full font-mono-data ${s.bg} ${s.color} border ${s.border}`}>{s.label}</span>
                </div>
                <div className="mb-3">
                  <div className="text-2xs text-muted-foreground font-mono-data mb-1.5">PROVIDERS</div>
                  <div className="flex flex-wrap gap-1">
                    {league.providers.map(p => (
                      <span key={p} className="text-2xs px-2 py-0.5 rounded bg-background border border-border text-muted-foreground">{p}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-2xs text-muted-foreground font-mono-data mb-1.5">MODULES</div>
                  <div className="flex flex-wrap gap-1">
                    {league.modules.map(m => (
                      <span key={m} className={`text-2xs px-2 py-0.5 rounded border ${league.status === 'active' ? 'bg-primary/10 border-primary/20 text-primary' : 'bg-card border-border text-muted-foreground'}`}>{m}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Providers */}
      {activeTab === 'providers' && (
        <div className="space-y-3">
          {PROVIDERS.map(provider => {
            const s = STATUS_CONFIG[provider.status];
            return (
              <div key={provider.id} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${provider.status === 'connected' ? 'bg-positive pulse-dot' : provider.status === 'ready' ? 'bg-yellow-400' : 'bg-muted-foreground'}`} />
                  <div>
                    <div className="text-sm font-semibold text-foreground">{provider.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {provider.sports.map(s => (
                        <span key={s} className="text-2xs px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-center">
                  <div>
                    <div className="text-sm font-mono-data text-foreground">{provider.endpoints}</div>
                    <div className="text-2xs text-muted-foreground">Endpoints</div>
                  </div>
                  <div>
                    <div className="text-sm font-mono-data text-foreground">{provider.rateLimit}</div>
                    <div className="text-2xs text-muted-foreground">Rate Limit</div>
                  </div>
                  <span className={`text-2xs px-2 py-0.5 rounded-full font-mono-data ${s.bg} ${s.color} border ${s.border}`}>{s.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Core Modules */}
      {activeTab === 'core' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CORE_MODULES.map((mod, i) => {
            const s = STATUS_CONFIG[mod.status as 'active' | 'ready'];
            return (
              <div key={i} className={`bg-card border rounded-xl p-4 ${mod.status === 'active' ? 'border-primary/20' : 'border-border'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${mod.status === 'active' ? 'bg-primary/10 border border-primary/30' : 'bg-card border border-border'}`}>
                    <Icon name={mod.icon as Parameters<typeof Icon>[0]['name']} size={16} className={mod.status === 'active' ? 'text-primary' : 'text-muted-foreground'} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-foreground">{mod.name}</span>
                      <span className={`text-2xs px-1.5 py-0.5 rounded font-mono-data ${s.bg} ${s.color} border ${s.border}`}>{s.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{mod.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Performance */}
      {activeTab === 'performance' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {PERF_METRICS.map((m, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">{m.label}</span>
                  <Icon name="CheckCircleIcon" size={14} className="text-positive" />
                </div>
                <div className="text-xl font-bold font-mono-data text-positive">{m.value}</div>
                <div className="text-2xs text-muted-foreground mt-0.5">Target: {m.target}</div>
              </div>
            ))}
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="BoltIcon" size={14} className="text-yellow-400" />
              Architecture Performance
            </h3>
            <div className="space-y-2 text-sm">
              {[
                { label: 'WebSockets', desc: 'Connexions persistantes pour données live, reconnexion auto 3s', icon: 'SignalIcon', color: 'text-primary' },
                { label: 'Edge Caching', desc: 'TTL adaptatif: 30s (live), 5min (standings), 1h (historical)', icon: 'ServerIcon', color: 'text-cyan-400' },
                { label: 'Worker Queues', desc: 'Calculs ML en background, non-bloquants, priorité adaptative', icon: 'QueueListIcon', color: 'text-purple-400' },
                { label: 'Parallel ML', desc: 'Monte Carlo parallélisé: 10k iterations en < 600ms', icon: 'CpuChipIcon', color: 'text-yellow-400' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-background border border-border">
                  <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={16} className={`${item.color} flex-shrink-0 mt-0.5`} />
                  <div>
                    <span className="font-medium text-foreground">{item.label}</span>
                    <span className="text-muted-foreground ml-2">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
